import React, { useState } from 'react';
import { User, Camera, CheckCircle, AlertCircle, Plus, X, Shield } from 'lucide-react';
import StepIndicator from './shared/StepIndicator';
import { FormInput, FormSelect, FormTextarea } from './shared/FormComponents';
import { LoadingButton } from './shared/LoadingComponents';
import WebcamCapture from './shared/WebcamCapture';
import { useToast } from '../../../hooks/useToast';
import { 
  REGISTRATION_STEPS, 
  BLOOD_GROUPS, 
  GENDER_OPTIONS, 
  BUTTON_STYLES, 
  CARD_STYLES 
} from '../utils/constants';
import { toast } from 'react-toastify';

const getDoctorDisplayLabel = (doctor = {}) => {
  const primaryName = typeof doctor.name === 'string' ? doctor.name.trim() : '';
  const fallbackName = [doctor.firstName, doctor.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const identityFallback = doctor.email || doctor.mobile || 'Doctor';
  const baseName = primaryName || fallbackName || identityFallback;
  const specialization = doctor.specialty || doctor.specialization || doctor.department;
  return specialization ? `${baseName} • ${specialization}` : baseName;
};

// Validation utilities
const validators = {
  name: (value) => {
    if (!value || value.trim().length < 2) return 'Name must be at least 2 characters';
    if (!/^[a-zA-Z\s]+$/.test(value)) return 'Name can only contain letters and spaces';
    if (value.length > 50) return 'Name cannot exceed 50 characters';
    return null;
  },
  
  mobile: (value) => {
    if (!value) return 'Phone number is required';
    const cleaned = value.replace(/[^\d]/g, '');
    if (cleaned.length < 10) return 'Phone number must be at least 10 digits';
    if (cleaned.length > 15) return 'Phone number cannot exceed 15 digits';
    if (!/^[\d\s\-\+\(\)]*$/.test(value)) return 'Invalid phone number format';
    return null;
  },
  
  email: (value) => {
    if (!value) return null; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return 'Please enter a valid email address';
    return null;
  },
  
  zipCode: (value) => {
    if (!value) return 'ZIP code is required';
    if (!/^\d{5,10}$/.test(value)) return 'ZIP code must be 5-10 digits';
    return null;
  },
  
  city: (value) => {
    if (!value || value.trim().length < 2) return 'City is required';
    if (!/^[a-zA-Z\s]+$/.test(value)) return 'City can only contain letters and spaces';
    return null;
  },
  
  state: (value) => {
    if (!value || value.trim().length < 2) return 'State is required';
    if (!/^[a-zA-Z\s]+$/.test(value)) return 'State can only contain letters and spaces';
    return null;
  },
  
  country: (value) => {
    if (!value || value.trim().length < 2) return 'Country is required';
    if (!/^[a-zA-Z\s]+$/.test(value)) return 'Country can only contain letters and spaces';
    return null;
  },
  
  dob: (value) => {
    if (!value) return 'Date of birth is required';
    const date = new Date(value);
    const today = new Date();
    const maxDate = new Date();
    const minDate = new Date();
    minDate.setFullYear(today.getFullYear() - 150); // Max age 150 years
    
    if (date > today) return 'Date of birth cannot be in the future';
    if (date < minDate) return 'Please enter a valid date of birth';
    return null;
  }
};

// Get today's date in YYYY-MM-DD format for max date
const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Get minimum date (150 years ago)
const getMinDate = () => {
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 150);
  return minDate.toISOString().split('T')[0];
};

// Validation state hook
const useValidation = (initialState = {}) => {
  const [errors, setErrors] = useState(initialState);
  
  const validateField = (field, value, validator) => {
    const error = validator ? validator(value) : null;
    setErrors(prev => ({ ...prev, [field]: error }));
    return !error;
  };
  
  const clearError = (field) => {
    setErrors(prev => ({ ...prev, [field]: null }));
  };
  
  const hasErrors = () => {
    return Object.values(errors).some(error => error !== null && error !== undefined && error !== '');
  };
  
  return { errors, validateField, clearError, hasErrors, setErrors };
};

// Array Input Component for managing lists (allergies, medications, etc.)
const ArrayInputField = ({ label, items = [], onAdd, onRemove, placeholder, maxLength = 100 }) => {
  const [inputValue, setInputValue] = useState('');
  
  const handleAdd = () => {
    if (inputValue.trim()) {
      onAdd(inputValue.trim());
      setInputValue('');
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };
  
  return (
    <div className="space-y-2">
      <label className="block text-md font-semibold text-gray-600 uppercase tracking-wide">{label}</label>
      <div className="flex gap-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          maxLength={maxLength}
          className="flex-1 px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium bg-white hover:border-[#2D869C] transition-all"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={!inputValue.trim()}
          className="px-4 py-2.5 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] text-white rounded-lg disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg font-semibold"
        >
          <Plus size={18} />
        </button>
      </div>
      {items && items.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {items.map((item, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#9DC9D4]/30 text-[#2D869C] rounded-full text-md font-medium"
            >
              {item}
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="hover:bg-[#2D869C]/20 rounded-full p-0.5 transition-colors"
              >
                <X size={16} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Move step components outside to prevent re-creation on each render
const PersonalDetailsStep = ({ 
  patientData, 
  setPatientData, 
  doctors, 
  loading, 
  onPatientDataStep 
}) => {
  const { errors, validateField, clearError, hasErrors } = useValidation();
  const { showSuccess, showError, showWarning, showInfo } = useToast();
  const [showMissingFieldsError, setShowMissingFieldsError] = useState(false);
  const [missingFieldsList, setMissingFieldsList] = useState([]);
  
  // Handle form field changes with validation
  const handleFieldChange = (field, value, validator) => {
    setPatientData({ ...patientData, [field]: value });
    if (value) {
      validateField(field, value, validator);
    } else {
      clearError(field);
    }
  };
  
  // Handle nested field changes (like personalAddress)
  const handleNestedFieldChange = (parentField, childField, value, validator) => {
    const updatedData = {
      ...patientData,
      [parentField]: { ...patientData[parentField], [childField]: value }
    };
    setPatientData(updatedData);
    
    if (value && validator) {
      validateField(`${parentField}.${childField}`, value, validator);
    } else {
      clearError(`${parentField}.${childField}`);
    }
  };
  
  // Format phone number as user types
  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 10) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    }
    return cleaned;
  };
  
  // Validate form before submission
  const validateForm = () => {
    const validations = [
      { field: 'firstName', value: patientData.firstName, validator: validators.name },
      { field: 'lastName', value: patientData.lastName, validator: validators.name },
      { field: 'mobile', value: patientData.mobile, validator: validators.mobile },
      { field: 'email', value: patientData.email, validator: validators.email },
      { field: 'dob', value: patientData.dob, validator: validators.dob },
      { field: 'personalAddress.city', value: patientData.personalAddress?.city, validator: validators.city },
      { field: 'personalAddress.state', value: patientData.personalAddress?.state, validator: validators.state },
      { field: 'personalAddress.zipCode', value: patientData.personalAddress?.zipCode, validator: validators.zipCode },
      { field: 'personalAddress.country', value: patientData.personalAddress?.country, validator: validators.country }
    ];
    
    let hasError = false;
    validations.forEach(({ field, value, validator }) => {
      const isValid = validateField(field, value, validator);
      if (!isValid) hasError = true;
    });
    
    // Check required fields
    if (!patientData.sex) {
      validateField('sex', '', () => 'Gender is required');
      hasError = true;
    }
    // Assigned doctor required
    if (!patientData.assignedDoctor) {
      validateField('assignedDoctor', '', () => 'Assigned Doctor is required');
      hasError = true;
    }
    
    if (!patientData.personalAddress?.street || patientData.personalAddress.street.trim().length < 3) {
      validateField('personalAddress.street', patientData.personalAddress?.street, () => 'Street address is required');
      hasError = true;
    }
    
    return !hasError;
  };
  
  // Handle form submission
  const handleSubmit = () => {
    // Check for missing required fields first
    const missingFields = [];
    
    if (!patientData.firstName) missingFields.push('First Name');
    if (!patientData.lastName) missingFields.push('Last Name');
    if (!patientData.mobile) missingFields.push('Phone Number');
    if (!patientData.dob) missingFields.push('Date of Birth');
    if (!patientData.sex) missingFields.push('Gender');
  if (!patientData.assignedDoctor) missingFields.push('Assigned Doctor');
    if (!patientData.personalAddress.street) missingFields.push('Street Address');
    if (!patientData.personalAddress.city) missingFields.push('City');
    if (!patientData.personalAddress.state) missingFields.push('State');
    if (!patientData.personalAddress.zipCode) missingFields.push('ZIP Code');
    if (!patientData.personalAddress.country) missingFields.push('Country');
    
    if (missingFields.length > 0) {
      const fieldsList = missingFields.join(', ');
      const errorMessage = `⚠️ Missing Required Fields:\n\n${missingFields.map((f, i) => `${i + 1}. ${f}`).join('\n')}\n\nPlease fill in all required fields to continue.`;
      
      // Show visual error banner
      setMissingFieldsList(missingFields);
      setShowMissingFieldsError(true);
      
      // Show toast notification
      showError(`Missing required fields: ${fieldsList}`, 8000);
      
      // Also show browser alert as fallback for visibility
      alert(errorMessage);
      
      // Scroll to top to see the error
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      return;
    }
    
    // Clear error banner if all fields are filled
    setShowMissingFieldsError(false);
    setMissingFieldsList([]);
    
    if (validateForm()) {
      showSuccess('✅ Personal details validated successfully!');
      onPatientDataStep();
    } else {
      const errorMessage = '⚠️ Please fix the validation errors in the form before continuing.';
      showError(errorMessage);
      alert(errorMessage);
    }
  };
  
  return (
  <div className="space-y-6" style={{ fontFamily: 'Poppins' }}>
    {/* Missing Fields Error Banner */}
    {showMissingFieldsError && missingFieldsList.length > 0 && (
      <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 shadow-sm">
        <div className="flex items-start">
          <AlertCircle className="h-6 w-6 text-red-500 flex-shrink-0" />
          <div className="ml-3 flex-1">
            <div className="flex items-center justify-between">
              <h3 className="text-md font-bold text-red-700 uppercase tracking-wide">
                Missing Required Fields ({missingFieldsList.length})
              </h3>
              <button
                onClick={() => setShowMissingFieldsError(false)}
                className="text-red-500 hover:text-red-700 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {missingFieldsList.map((field, index) => (
                <span key={index} className="text-sm font-medium text-red-700 bg-red-100 px-3 py-1 rounded-lg">
                  {field}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Main Registration Card */}
    <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden p-6">
      {/* Section Header */}
      <h3 className="text-2xl font-bold text-gray-700 mb-6" style={{ fontFamily: 'sans-serif' }}>Patient Details</h3>

      <div className="space-y-4">
        {/* Row 1: First Name, Last Name, Phone Number */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">First Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={patientData.firstName}
              onChange={(e) => handleFieldChange('firstName', e.target.value, validators.name)}
              className={`w-full px-4 py-2.5 border-2 ${errors.firstName ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium hover:border-[#2D869C] transition-all`}
            />
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Last Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={patientData.lastName}
              onChange={(e) => handleFieldChange('lastName', e.target.value, validators.name)}
              className={`w-full px-4 py-2.5 border-2 ${errors.lastName ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium hover:border-[#2D869C] transition-all`}
            />
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Phone Number <span className="text-red-500">*</span></label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2D869C] text-lg">📞</span>
              <input
                type="tel"
                value={patientData.mobile}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^\d\s\-\+\(\)]/g, '');
                  handleFieldChange('mobile', value, validators.mobile);
                }}
                className={`w-full pl-10 pr-4 py-2.5 border-2 ${errors.mobile ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium hover:border-[#2D869C] transition-all`}
              />
            </div>
          </div>
        </div>

        {/* Row 2: DOB, Gender, Blood Group, Email */}
        <div className="grid grid-cols-4 gap-4">
          <div>
            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Date of Birth <span className="text-red-500">*</span></label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2D869C] text-lg">📅</span>
              <input
                type="date"
                value={patientData.dob}
                onChange={(e) => handleFieldChange('dob', e.target.value, validators.dob)}
                max={getTodayDate()}
                min={getMinDate()}
                className={`w-full pl-10 pr-4 py-2.5 border-2 ${errors.dob ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium text-gray-700 hover:border-[#2D869C] transition-all`}
              />
            </div>
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Gender <span className="text-red-500">*</span></label>
            <select
              value={patientData.sex}
              onChange={(e) => {
                setPatientData({ ...patientData, sex: e.target.value });
                if (e.target.value) clearError('sex');
              }}
              className={`w-full px-4 py-2.5 border-2 ${errors.sex ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium bg-white text-gray-700 hover:border-[#2D869C] transition-all`}
            >
              {GENDER_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value} className="text-gray-700">{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Blood Group</label>
            <select
              value={patientData.bloodGroup}
              onChange={(e) => setPatientData({ ...patientData, bloodGroup: e.target.value })}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium bg-white text-gray-700 hover:border-[#2D869C] transition-all"
            >
              {BLOOD_GROUPS.map(opt => (
                <option key={opt.value} value={opt.value} className="text-gray-700">{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Email</label>
            <input
              type="email"
              value={patientData.email}
              onChange={(e) => handleFieldChange('email', e.target.value, validators.email)}
              className={`w-full px-4 py-2.5 border-2 ${errors.email ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium hover:border-[#2D869C] transition-all`}
            />
          </div>
        </div>

        {/* Row 3: Street Address (bigger), City, State, Country, Zip Code */}
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-2">
            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Street Address <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={patientData.personalAddress.street}
              onChange={(e) => handleNestedFieldChange('personalAddress', 'street', e.target.value)}
              className={`w-full px-4 py-2.5 border-2 ${errors['personalAddress.street'] ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium hover:border-[#2D869C] transition-all`}
            />
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">City <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={patientData.personalAddress.city}
              onChange={(e) => handleNestedFieldChange('personalAddress', 'city', e.target.value, validators.city)}
              className={`w-full px-4 py-2.5 border-2 ${errors['personalAddress.city'] ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium hover:border-[#2D869C] transition-all`}
            />
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">State <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={patientData.personalAddress.state}
              onChange={(e) => handleNestedFieldChange('personalAddress', 'state', e.target.value, validators.state)}
              className={`w-full px-4 py-2.5 border-2 ${errors['personalAddress.state'] ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium hover:border-[#2D869C] transition-all`}
            />
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Country <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={patientData.personalAddress.country}
              onChange={(e) => handleNestedFieldChange('personalAddress', 'country', e.target.value, validators.country)}
              className={`w-full px-4 py-2.5 border-2 ${errors['personalAddress.country'] ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium hover:border-[#2D869C] transition-all`}
            />
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Zip Code <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={patientData.personalAddress.zipCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                handleNestedFieldChange('personalAddress', 'zipCode', value, validators.zipCode);
              }}
              maxLength="10"
              className={`w-full px-4 py-2.5 border-2 ${errors['personalAddress.zipCode'] ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium hover:border-[#2D869C] transition-all`}
            />
          </div>
        </div>

        {/* Row 4: Assigned Doctor, Medical Notes */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Assigned Doctor <span className="text-red-500">*</span></label>
            <select
              value={patientData.assignedDoctor}
              onChange={(e) => {
                setPatientData({ ...patientData, assignedDoctor: e.target.value });
                if (e.target.value) clearError('assignedDoctor');
              }}
              className={`w-full px-4 py-2.5 border-2 ${errors.assignedDoctor ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium bg-white text-gray-700 hover:border-[#2D869C] transition-all`}
            >
              <option value="" className="text-gray-700">Select doctor</option>
              {doctors.map((doctor) => (
                <option key={doctor._id || doctor.id} value={doctor._id || doctor.id} className="text-gray-700">
                  {getDoctorDisplayLabel(doctor)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Medical Notes</label>
            <div className="relative">
              <input
                type="text"
                value={patientData.medicalNotes || ''}
                onChange={(e) => setPatientData({ ...patientData, medicalNotes: e.target.value })}
                maxLength={1000}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium hover:border-[#2D869C] transition-all"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">
                {(patientData.medicalNotes || '').length}/1000
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Medical History Section */}
      <div className="mt-6 border-t-2 border-gray-200 pt-6">
        <h4 className="text-xl font-bold text-gray-700 mb-4" style={{ fontFamily: 'sans-serif' }}>Medical History</h4>
        
        <div className="grid grid-cols-3 gap-4 mb-4">
          {/* Drug Allergies */}
          <ArrayInputField
            label="Drug Allergies"
            items={patientData.drugAllergies || []}
            onAdd={(item) => setPatientData({ 
              ...patientData, 
              drugAllergies: [...(patientData.drugAllergies || []), item] 
            })}
            onRemove={(index) => setPatientData({ 
              ...patientData, 
              drugAllergies: (patientData.drugAllergies || []).filter((_, i) => i !== index) 
            })}
            placeholder="e.g., Penicillin"
          />

          {/* General Allergies */}
          <ArrayInputField
            label="General Allergies"
            items={patientData.allergies || []}
            onAdd={(item) => setPatientData({ 
              ...patientData, 
              allergies: [...(patientData.allergies || []), item] 
            })}
            onRemove={(index) => setPatientData({ 
              ...patientData, 
              allergies: (patientData.allergies || []).filter((_, i) => i !== index) 
            })}
            placeholder="e.g., Peanuts"
          />

          {/* Current Medications */}
          <ArrayInputField
            label="Current Medication"
            items={patientData.currentMedications || []}
            onAdd={(item) => setPatientData({ 
              ...patientData, 
              currentMedications: [...(patientData.currentMedications || []), item] 
            })}
            onRemove={(index) => setPatientData({ 
              ...patientData, 
              currentMedications: (patientData.currentMedications || []).filter((_, i) => i !== index) 
            })}
            placeholder="e.g., Lisinopril 10mg"
          />
        </div>

        {/* Past Medical History */}
        <div>
          <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Past Medical History</label>
          <textarea
            value={patientData.pastHistory || ''}
            onChange={(e) => setPatientData({ ...patientData, pastHistory: e.target.value })}
            placeholder="e.g., Hypertension, diabetes mellitus type 2"
            rows={3}
            maxLength={1000}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium resize-none hover:border-[#2D869C] transition-all"
          />
          <div className="text-sm font-medium text-gray-600 text-right mt-1">
            {(patientData.pastHistory || '').length}/1000
          </div>
        </div>
      </div>

      {/* Continue Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="px-6 py-3 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:scale-105 disabled:from-gray-300 disabled:to-gray-300 disabled:transform-none disabled:cursor-not-allowed text-lg"
        >
          <span style={{ fontFamily: 'sans-serif' }}>{loading ? 'Processing...' : 'Continue'}</span>
        </button>
      </div>
    </div>
  </div>
  );
};

const InsuranceDetailsStep = ({
  patientData,
  setPatientData,
  onNext,
  onBack
}) => {
  return (
    <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden p-6" style={{ fontFamily: 'Poppins' }}>
      <div className="flex items-center space-x-3 mb-6">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-gradient-to-r from-[#2D869C]/20 to-[#3a9fb5]/10 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-[#2D869C]" />
          </div>
        </div>
        <div>
          <h3 className="text-2xl font-bold text-gray-700" style={{ fontFamily: 'sans-serif' }}>Insurance Details (Optional)</h3>
          <p className="text-md font-medium text-gray-600">Enter insurance policy information for Form A reimbursement claims.</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Policy Number</label>
            <input
              type="text"
              value={patientData.policyNumber || ''}
              onChange={(e) => setPatientData({ ...patientData, policyNumber: e.target.value })}
              placeholder="Enter policy number"
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium hover:border-[#2D869C] transition-all"
            />
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">SI/Certificate Number</label>
            <input
              type="text"
              value={patientData.certificateNumber || ''}
              onChange={(e) => setPatientData({ ...patientData, certificateNumber: e.target.value })}
              placeholder="Enter certificate number"
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium hover:border-[#2D869C] transition-all"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Company/TPA Number</label>
            <input
              type="text"
              value={patientData.companyTpaId || ''}
              onChange={(e) => setPatientData({ ...patientData, companyTpaId: e.target.value })}
              placeholder="Enter company/TPA number"
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium hover:border-[#2D869C] transition-all"
            />
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">ABHA Number</label>
            <input
              type="text"
              value={patientData.abhaNumber || ''}
              onChange={(e) => setPatientData({ ...patientData, abhaNumber: e.target.value })}
              placeholder="Enter ABHA number"
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium hover:border-[#2D869C] transition-all"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Insurance Start Date</label>
            <input
              type="date"
              value={patientData.insuranceCommencementDate || ''}
              onChange={(e) => setPatientData({ ...patientData, insuranceCommencementDate: e.target.value })}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium text-gray-700 hover:border-[#2D869C] transition-all"
            />
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Sum Insured (Rs)</label>
            <input
              type="number"
              value={patientData.sumInsured || ''}
              onChange={(e) => setPatientData({ ...patientData, sumInsured: e.target.value })}
              placeholder="Enter sum insured amount"
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium hover:border-[#2D869C] transition-all"
            />
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-between items-center">
        <button
          onClick={onBack}
          className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-all shadow-sm hover:shadow"
        >
          <span style={{ fontFamily: 'sans-serif' }}>Back</span>
        </button>
        <div className="flex gap-4 items-center">
          <button
            onClick={onNext}
            className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-all shadow-sm hover:shadow"
          >
            <span style={{ fontFamily: 'sans-serif' }}>Skip</span>
          </button>
          <button
            onClick={onNext}
            className="px-6 py-3 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:scale-105 text-lg"
          >
            <span style={{ fontFamily: 'sans-serif' }}>Continue</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const PhotoAndCompleteStep = ({ 
  patientData,
  setPatientData,
  patientPhoto,
  setPatientPhoto,
  loading, 
  onCompleteRegistration,
  onResetRegistrationForm,
  setRegistrationStep 
}) => {
  const [registrationError, setRegistrationError] = useState(null);
  const { showSuccess, showError, showWarning, showInfo } = useToast();
  
  const handleCompleteRegistration = async () => {
    if (loading) return; // Prevent double submit
    try {
      setRegistrationError(null);
      await onCompleteRegistration();
      // Show success toast after successful registration
    } catch (error) {
      console.error('Registration failed:', error);
      const status = error.response?.status;
      if (status === 409) {
        // Friendly duplicate message for non-technical users
        setRegistrationError('Patient already exists. A record with the same name, date of birth and phone number was found.');
        return; // Do not rethrow; suppress external toast
      }
      const rawMsg = error.response?.data?.message || error.message;
      setRegistrationError(rawMsg ? `Registration failed: ${rawMsg}` : 'Registration failed. Please try again.');
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };
  
  const handleVitalChange = (field, value) => {
    const numericValue = value === '' ? '' : parseFloat(value);
    const updatedData = {
      ...patientData,
      vitals: {
        ...patientData.vitals,
        [field]: numericValue
      }
    };
    
    // Auto-calculate BMI when height and weight are provided
    if (field === 'heightCm' || field === 'weightKg') {
      const height = field === 'heightCm' ? numericValue : patientData.vitals?.heightCm;
      const weight = field === 'weightKg' ? numericValue : patientData.vitals?.weightKg;
      
      if (height && weight && height > 0) {
        const heightM = height / 100;
        const bmi = Math.round((weight / (heightM * heightM)) * 100) / 100;
        updatedData.vitals.bmi = bmi;
      }
    }
    
    // Set data through parent component if setPatientData is available
    if (typeof setPatientData === 'function') {
      setPatientData(updatedData);
    }
  };

  return (
  <div className="space-y-6">
    {/* Photo Capture Section */}
    <WebcamCapture
      onPhotoCapture={setPatientPhoto}
      onPhotoRemove={() => setPatientPhoto(null)}
      capturedPhoto={patientPhoto}
      loading={loading}
      disabled={loading}
    />

    {/* Patient Summary */}
    <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden p-6" style={{ fontFamily: 'Poppins' }}>
      <div className="flex items-center space-x-3 mb-6">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-gradient-to-r from-green-500/20 to-green-400/10 rounded-xl flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
        </div>
        <div>
          <h3 className="text-2xl font-bold text-gray-700" style={{ fontFamily: 'sans-serif' }}>Registration Summary</h3>
          <p className="text-md font-medium text-gray-600">Review patient information before completing registration</p>
        </div>
      </div>

      {/* Registration Error */}
      {registrationError && (
        <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-500" />
            </div>
            <div className="ml-3">
              <h3 className="text-md font-bold text-red-700 uppercase tracking-wide">Registration Failed</h3>
              <div className="mt-2 text-md text-red-600 font-medium">
                <p>{registrationError}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-md font-semibold text-gray-600">Full Name:</span>
            <span className="text-md text-gray-700 font-medium">{patientData.firstName} {patientData.lastName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-md font-semibold text-gray-600">Phone:</span>
            <span className="text-md text-gray-700 font-medium">{patientData.mobile}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-md font-semibold text-gray-600">Email:</span>
            <span className="text-md text-gray-700 font-medium">{patientData.email || 'Not provided'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-md font-semibold text-gray-600">Date of Birth:</span>
            <span className="text-md text-gray-700 font-medium">{formatDate(patientData.dob)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-md font-semibold text-gray-600">Gender:</span>
            <span className="text-md text-gray-700 font-medium">{patientData.sex ? patientData.sex.charAt(0).toUpperCase() + patientData.sex.slice(1) : 'Not specified'}</span>
          </div>
        </div>
        
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-md font-semibold text-gray-600">Address:</span>
            <span className="text-md text-gray-700 font-medium text-right">
              {patientData.personalAddress.street}, {patientData.personalAddress.city}
              {patientData.personalAddress.state && `, ${patientData.personalAddress.state}`}
              {patientData.personalAddress.zipCode && ` ${patientData.personalAddress.zipCode}`}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-md font-semibold text-gray-600">Blood Group:</span>
            <span className="text-md text-gray-700 font-medium">{patientData.bloodGroup || 'Not specified'}</span>
          </div>
          {/* Vitals Summary */}
          {patientData.vitals && Object.keys(patientData.vitals).some(key => patientData.vitals[key] !== null && patientData.vitals[key] !== '') && (
            <div className="flex justify-between">
              <span className="text-md font-semibold text-gray-600">Vitals Recorded:</span>
              <span className="text-md text-green-600 font-bold">✓ Yes</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-md font-semibold text-gray-600">Photo:</span>
            <span className="text-md text-gray-700 font-medium">{patientPhoto ? 'Captured' : 'Not provided'}</span>
          </div>
        </div>
      </div>

      {/* Detailed Vitals Summary */}
      {patientData.vitals && Object.keys(patientData.vitals).some(key => patientData.vitals[key] !== null && patientData.vitals[key] !== '' && patientData.vitals[key] !== undefined) && (
        <div className="mt-6 bg-green-50 border-2 border-green-200 rounded-xl p-4 shadow-sm">
          <h5 className="text-lg font-bold text-green-700 mb-3 flex items-center uppercase tracking-wide" style={{ fontFamily: 'sans-serif' }}>
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Recorded Vitals
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-md">
            {patientData.vitals.heightCm && (
              <div>
                <span className="font-semibold text-gray-600">Height:</span>
                <span className="ml-2 text-gray-700 font-medium">{patientData.vitals.heightCm} cm</span>
              </div>
            )}
            {patientData.vitals.weightKg && (
              <div>
                <span className="font-semibold text-gray-600">Weight:</span>
                <span className="ml-2 text-gray-700 font-medium">{patientData.vitals.weightKg} kg</span>
              </div>
            )}
            {patientData.vitals.bmi && (
              <div>
                <span className="font-semibold text-gray-600">BMI:</span>
                <span className="ml-2 text-gray-700 font-medium">{patientData.vitals.bmi}</span>
              </div>
            )}
            {(patientData.vitals.bpSys || patientData.vitals.bpDia) && (
              <div>
                <span className="font-semibold text-gray-600">Blood Pressure:</span>
                <span className="ml-2 text-gray-700 font-medium">
                  {patientData.vitals.bpSys || '--'}/{patientData.vitals.bpDia || '--'} mmHg
                </span>
              </div>
            )}
            {patientData.vitals.pulse && (
              <div>
                <span className="font-semibold text-gray-600">Pulse:</span>
                <span className="ml-2 text-gray-700 font-medium">{patientData.vitals.pulse} bpm</span>
              </div>
            )}
            {patientData.vitals.temperature && (
              <div>
                <span className="font-semibold text-gray-600">Temperature:</span>
                <span className="ml-2 text-gray-700 font-medium">{patientData.vitals.temperature}°F</span>
              </div>
            )}
            {patientData.vitals.oxygenSaturation && (
              <div>
                <span className="font-semibold text-gray-600">Oxygen Saturation:</span>
                <span className="ml-2 text-gray-700 font-medium">{patientData.vitals.oxygenSaturation}%</span>
              </div>
            )}
            {patientData.vitals.respiration && (
              <div>
                <span className="font-semibold text-gray-600">Respiration:</span>
                <span className="ml-2 text-gray-700 font-medium">{patientData.vitals.respiration} breaths/min</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Insurance Summary */}
      {(patientData.policyNumber || patientData.certificateNumber || patientData.companyTpaId || patientData.abhaNumber || patientData.insuranceCommencementDate || patientData.sumInsured) && (
        <div className="mt-6 bg-[#2D869C]/10 border-2 border-[#2D869C]/30 rounded-xl p-4 shadow-sm">
          <h5 className="text-lg font-bold text-[#2D869C] mb-3 flex items-center uppercase tracking-wide" style={{ fontFamily: 'sans-serif' }}>
            <Shield className="w-5 h-5 mr-2" />
            Insurance Details
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-md">
            {patientData.policyNumber && (
              <div>
                <span className="font-semibold text-gray-600">Policy Number:</span>
                <span className="ml-2 text-gray-700 font-medium">{patientData.policyNumber}</span>
              </div>
            )}
            {patientData.certificateNumber && (
              <div>
                <span className="font-semibold text-gray-600">Certificate Number:</span>
                <span className="ml-2 text-gray-700 font-medium">{patientData.certificateNumber}</span>
              </div>
            )}
            {patientData.companyTpaId && (
              <div>
                <span className="font-semibold text-gray-600">Company/TPA ID:</span>
                <span className="ml-2 text-gray-700 font-medium">{patientData.companyTpaId}</span>
              </div>
            )}
            {patientData.abhaNumber && (
              <div>
                <span className="font-semibold text-gray-600">ABHA Number:</span>
                <span className="ml-2 text-gray-700 font-medium">{patientData.abhaNumber}</span>
              </div>
            )}
            {patientData.insuranceCommencementDate && (
              <div>
                <span className="font-semibold text-gray-600">Insurance Start Date:</span>
                <span className="ml-2 text-gray-700 font-medium">{formatDate(patientData.insuranceCommencementDate)}</span>
              </div>
            )}
            {patientData.sumInsured && (
              <div>
                <span className="font-semibold text-gray-600">Sum Insured:</span>
                <span className="ml-2 text-gray-700 font-medium">₹{patientData.sumInsured}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Medical History Summary */}
      {(
        (patientData.drugAllergies && patientData.drugAllergies.length > 0) ||
        (patientData.allergies && patientData.allergies.length > 0) ||
        (patientData.currentMedications && patientData.currentMedications.length > 0) ||
        patientData.pastHistory
      ) && (
        <div className="mt-6 bg-white border-2 border-gray-200 rounded-xl p-4 shadow-sm">
          <h5 className="text-lg font-bold text-gray-700 mb-3 flex items-center uppercase tracking-wide" style={{ fontFamily: 'sans-serif' }}>
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Medical History
          </h5>
          <div className="space-y-3 text-md">
            {patientData.drugAllergies && patientData.drugAllergies.length > 0 && (
              <div>
                <span className="font-bold text-gray-700">Drug Allergies:</span>
                <div className="mt-1 flex flex-wrap gap-2">
                  {patientData.drugAllergies.map((allergy, index) => (
                    <span key={index} className="inline-block px-3 py-1.5 bg-red-200 text-red-800 font-semibold rounded-lg text-sm">
                      {allergy}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {patientData.allergies && patientData.allergies.length > 0 && (
              <div>
                <span className="font-bold text-gray-700">General Allergies:</span>
                <div className="mt-1 flex flex-wrap gap-2">
                  {patientData.allergies.map((allergy, index) => (
                    <span key={index} className="inline-block px-3 py-1.5 bg-orange-200 text-orange-800 font-semibold rounded-lg text-sm">
                      {allergy}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {patientData.currentMedications && patientData.currentMedications.length > 0 && (
              <div>
                <span className="font-bold text-gray-700">Current Medications:</span>
                <div className="mt-1 flex flex-wrap gap-2">
                  {patientData.currentMedications.map((medication, index) => (
                    <span key={index} className="inline-block px-3 py-1.5 bg-green-200 text-green-800 font-semibold rounded-lg text-sm">
                      {medication}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {patientData.pastHistory && (
              <div>
                <span className="font-bold text-gray-700">Past Medical History:</span>
                <p className="mt-1 text-gray-700 font-medium whitespace-pre-wrap">{patientData.pastHistory}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 flex justify-between">
        <button
          onClick={() => setRegistrationStep(2)}
          className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-all shadow-sm hover:shadow"
          disabled={loading}
        >
          <span style={{ fontFamily: 'sans-serif' }}>Back to Edit</span>
        </button>
        <div className="flex gap-4">
          <button
            onClick={onResetRegistrationForm}
            className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:scale-105 text-lg"
            disabled={loading}
          >
            <span style={{ fontFamily: 'sans-serif' }}>Cancel Registration</span>
          </button>
          <button
            onClick={handleCompleteRegistration}
            disabled={loading}
            className="px-6 py-3 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:scale-105 disabled:from-gray-300 disabled:to-gray-300 disabled:transform-none disabled:cursor-not-allowed text-lg"
          >
            <span style={{ fontFamily: 'sans-serif' }}>{loading ? 'Creating Patient Record...' : 'Complete Registration'}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
  );
};

const PatientRegistration = ({
  registrationStep,
  setRegistrationStep,
  patientData,
  setPatientData,
  patientPhoto,
  setPatientPhoto,
  loading,
  error,
  doctors,
  onPatientDataStep,
  onCompleteRegistration,
  onResetRegistrationForm
}) => {
  const { showSuccess, showError, showWarning, showInfo } = useToast(); // retained for other steps; Photo step now suppresses toast usage
  return (
    <div className="flex-1 p-6">
      {/* Error Message (only show for step 1 to avoid duplicate with inline banner in step 2) */}
      {error && registrationStep === 1 && (
        <div className="mb-4 bg-theme-error/10 border border-theme-error/30 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-theme-error" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-theme-error">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Step Indicator */}
      <StepIndicator 
        steps={REGISTRATION_STEPS} 
        currentStep={registrationStep} 
        className="mb-6"
      />

      {/* Step Content */}
      <div className="max-w-6xl mx-auto">
        {registrationStep === 1 && (
          <PersonalDetailsStep 
            patientData={patientData}
            setPatientData={setPatientData}
            doctors={doctors}
            loading={loading}
            onPatientDataStep={onPatientDataStep}
          />
        )}
        {registrationStep === 2 && (
          <InsuranceDetailsStep
            patientData={patientData}
            setPatientData={setPatientData}
            onNext={() => setRegistrationStep(3)}
            onBack={() => setRegistrationStep(1)}
          />
        )}
        {registrationStep === 3 && (
          <PhotoAndCompleteStep 
            patientData={patientData}
            setPatientData={setPatientData}
            patientPhoto={patientPhoto}
            setPatientPhoto={setPatientPhoto}
            loading={loading}
            onCompleteRegistration={onCompleteRegistration}
            onResetRegistrationForm={onResetRegistrationForm}
            setRegistrationStep={setRegistrationStep}
          />
        )}
      </div>
    </div>
  );
};

export default PatientRegistration;